import { computed, Ref, ref } from 'vue';
import { useStore } from 'vuex';
import { ContentsContext, Feature, PageName } from '@/api/tracking';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import {
  ActionModal,
  AdpDocument,
  AdpDocumentOptionAction,
  AdpDocumentOptionActionName,
  Group,
  Mention,
} from '@/types';
import { useComment } from '@/utils/actions/comment';
import { useGroupMark } from '@/utils/actions/group-mark';
import { useMark } from '@/utils/actions/mark';
import { useAdpDocumentMarkPresentation } from '@/utils/composables/useAdpDocumentMarkPresentation';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useEmitter } from '@/utils/vue';

interface TrackingProps {
  pageName: PageName;
  feature: Feature;
  rankInWholeFeed: number;
  contentsContext: ContentsContext;
}

/**
 * survey のドキュメントカード（記事カード・レポートカード）で共有する
 * マーク / グループマーク / コメントのアクションロジック。
 */
export const useSurveyDocumentActions = (
  adpDocument: Ref<AdpDocument>,
  tracking: TrackingProps,
  emit: (e: 'update-document', doc: AdpDocument) => void,
) => {
  const store = useStore();
  const emitter = useEmitter();
  const { createSnackbar } = useSnackbar();
  const { getCurrentPathForTracking } = useCurrentPathForTracking();

  const trackingBaseData = {
    pageName: tracking.pageName,
    pageUrl: getCurrentPathForTracking(),
    feature: tracking.feature,
  };

  const { createMark, removeMark } = useMark({
    trackingBaseData,
    rankInWholeFeed: tracking.rankInWholeFeed,
    contentsContext: tracking.contentsContext,
  });

  const { createGroupMark } = useGroupMark({
    trackingBaseData,
    rankInWholeFeed: tracking.rankInWholeFeed,
    contentsContext: tracking.contentsContext,
  });

  const { createComment } = useComment({
    trackingBaseData,
    rankInWholeFeed: tracking.rankInWholeFeed,
    contentsContext: tracking.contentsContext,
  });

  const actionModal = ref<ActionModal>(undefined);
  const isPostingComment = ref(false);

  const userInfo = computed(() => store.state.userInfo.userInfo);

  const { isMarked, markedUsers, markedGroups, commentedUsers } =
    useAdpDocumentMarkPresentation(adpDocument);

  const copyLinkUrl = async () => {
    try {
      // NOTE: 既存のコピー機能で使用している Clipboard JS は非推奨の`document.execCommand`を使用しているため、使わない
      // クリップボード API: https://developer.mozilla.org/ja/docs/Web/API/Clipboard_API
      await navigator.clipboard.writeText(adpDocument.value.url);
      createSnackbar({
        message: 'リンクをコピーしました',
        type: 'success',
      });
    } catch (_e) {
      createSnackbar({
        message: 'リンクをコピーできませんでした',
        type: 'error',
      });
    }
  };

  const optionActions: Array<AdpDocumentOptionAction> = [
    {
      label: 'リンクをコピー',
      icon: 'chain',
      emitName: 'copy-link',
    },
  ];

  const handleOptionAction = async (emitName: AdpDocumentOptionActionName) => {
    if (emitName === 'copy-link') {
      await copyLinkUrl();
    }
  };

  const handleCommentButton = () => {
    actionModal.value = 'comment';
  };

  const handleGroupMarkButton = () => {
    actionModal.value = 'group_mark';
  };

  const handlePopupMenu = () => {
    actionModal.value = 'mark';
  };

  const closeActionModal = () => {
    actionModal.value = undefined;
  };

  const handleMarkButton = async (markProps?: {
    tags?: string[];
    memo?: string;
  }) => {
    if (!userInfo.value) return;

    if (isMarked.value) {
      await removeMark({
        adpDocument: adpDocument.value,
        userInfo: userInfo.value,
        afterUndo: () => {
          emitter.emit('add-article-for-article-list', adpDocument.value);
          emitter.emit('article-updated', adpDocument.value);
        },
      });
      emitter.emit('remove-article-for-article-list', adpDocument.value);
      emitter.emit('article-updated', adpDocument.value);
    } else {
      await createMark({
        adpDocument: adpDocument.value,
        userInfo: userInfo.value,
        tags: markProps?.tags,
        memo: markProps?.memo,
      });
      emitter.emit('add-article-for-article-list', adpDocument.value);
      emitter.emit('article-updated', adpDocument.value);
      closeActionModal();
    }
    emit('update-document', adpDocument.value);
  };

  const groupMarkArticle = async ({
    group,
    tags,
    memo,
  }: {
    group: Group;
    tags?: string[];
    memo?: string;
  }) => {
    await createGroupMark({
      adpDocument: adpDocument.value,
      group: group,
      tags: tags,
      memo: memo,
    });
    closeActionModal();
    emitter.emit('article-updated', adpDocument.value);
  };

  const commentArticle = async ({
    group,
    content,
    mention,
  }: {
    group: Group;
    content: string;
    mention?: Mention;
  }) => {
    try {
      isPostingComment.value = true;
      const createdComment = await createComment({
        groupId: group.id,
        adpDocument: adpDocument.value,
        content,
        mention,
      });
      emitter.emit('article-updated', adpDocument.value);
      emitter.emit('comment-created', {
        ...createdComment,
        article: adpDocument.value,
      });
      closeActionModal();
    } finally {
      isPostingComment.value = false;
    }
  };

  return {
    actionModal,
    isPostingComment,
    isMarked,
    markedUsers,
    markedGroups,
    commentedUsers,
    optionActions,
    handleOptionAction,
    handleCommentButton,
    handleGroupMarkButton,
    handlePopupMenu,
    closeActionModal,
    handleMarkButton,
    groupMarkArticle,
    commentArticle,
  };
};
